/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Measure from 'react-measure';
import ReactModal from 'react-modal';

import classNames from 'classnames';

import type { Theme } from '@coursera/cds-core';
import { Button, Typography } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';
import type { ApiButtonProps } from '@coursera/coursera-ui';
import { Box } from '@coursera/coursera-ui';

import ModalButtonFooter from 'bundles/authoring/common/modals/cds/ModalButtonFooter';

import _t from 'i18n!nls/authoring';

import 'css!./__styles__/StaticModal';

type Props = {
  className?: string;
  title: string;
  overlayClassName?: string;
  hideFooter?: boolean;
  onClose?: () => void;
  screenReaderLabel?: string;
  includePrimaryButton?: boolean;
  enablePrimaryButton?: boolean;
  primaryButtonContents?: string;
  onPrimaryButtonClick?: () => void;
  includeSecondaryButton?: boolean;
  enableSecondaryButton?: boolean;
  secondaryButtonContents?: string;
  onSecondaryButtonClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  disabledPrimaryExplanation?: string;
  primaryApiButtonProps?: ApiButtonProps;
};

const styles = {
  header: (theme: Theme) =>
    css({
      paddingBottom: 'var(--cds-spacing-400)',
    }),
};

export default class StaticModal extends React.Component<Props> {
  static defaultProps = {
    className: '',
    overlayClassName: '',
    hideFooter: false,
    onClose: () => {},
    onSave: () => {},
    onPrimaryButtonClick: () => {},
    includePrimaryButton: true,
    enablePrimaryButton: false,
    includeSecondaryButton: true,
    enableSecondaryButton: true,
  };

  UNSAFE_componentWillMount() {
    ReactModal.setAppElement('#rendered-content');
  }

  render() {
    const {
      children,
      title,
      hideFooter,
      className,
      screenReaderLabel,
      overlayClassName,
      onClose,
      includePrimaryButton,
      enablePrimaryButton,
      primaryButtonContents,
      onPrimaryButtonClick,
      includeSecondaryButton,
      enableSecondaryButton,
      secondaryButtonContents,
      onSecondaryButtonClick,
      disabledPrimaryExplanation,
      primaryApiButtonProps,
    } = this.props;

    return (
      <ReactModal
        isOpen={true}
        className={`rc-StaticModal ${className}`}
        contentLabel={screenReaderLabel && _t('Modal: #{screenReaderLabel}', { screenReaderLabel })}
        overlayClassName={`static-modal-overlay ${overlayClassName}`}
        onRequestClose={onClose}
      >
        <Box rootClassName="static-modal-container" flexDirection="column" justifyContent="between">
          <Box flexDirection="row" justifyContent="end">
            <Button
              onClick={onClose}
              variant="ghost"
              size="small"
              icon={<CloseIcon size="small" />}
              iconPosition="after"
            >
              {_t('Close')}
            </Button>
          </Box>
          <Typography variant="h1semibold" component="h2" css={styles.header}>
            {title}
          </Typography>
          <Measure client scroll>
            {({ measureRef, contentRect, measure }) => {
              return (
                <div
                  className={classNames('static-modal-children', {
                    'top-border': !!contentRect.scroll?.top,
                    'bottom-border': contentRect.scroll?.height !== contentRect.client?.height,
                  })}
                  ref={measureRef}
                  onScroll={() => measure()}
                >
                  {children}
                </div>
              );
            }}
          </Measure>
          {!hideFooter && (
            <ModalButtonFooter
              isPrimaryButtonDisabled={!enablePrimaryButton}
              {...(includePrimaryButton && { primaryButtonContents: primaryButtonContents || _t('Save') })}
              onPrimaryButtonClick={onPrimaryButtonClick}
              isSecondaryButtonDisabled={!enableSecondaryButton}
              {...(includeSecondaryButton && { secondaryButtonContents: secondaryButtonContents || _t('Cancel') })}
              onSecondaryButtonClick={onSecondaryButtonClick || onClose}
              disabledPrimaryExplanation={disabledPrimaryExplanation}
              explanationClassName="static-modal-disabled-primary-explanation"
              primaryApiButtonProps={primaryApiButtonProps}
            />
          )}
        </Box>
      </ReactModal>
    );
  }
}
